import { useEffect, useMemo, useState } from 'react';
import {
  getIvtClient,
  GetSolutionMapAllCommand,
  CreateAssetCommand,
  LinkUcAssetCommand,
  AssetOrigin,
} from '../../../client/IvtHubClient';
import { topicClient } from '../../../client/topicClient';
import type { Topic, TopicInstance } from '../../../client/IvtHubClient';
import { SPAN_AREA, displayAreaName } from '../../SolutionMapPage/utils/spanCoverage';
import { denialHint } from '../../../utils/authorization';

/** The Industry <select> only needs the name; GetSolutionMapAll carries no
 *  assetCount/activeMotionCount (that's ListIndustries' shape), so we model the
 *  dropdown rows minimally rather than fabricate those counts. */
type IndustryOption = { industry: string };

/** One selectable Use Case in the dropdown. The link is by solutionMapId (the UC
 *  primary key = SolutionMapEntry.id), NOT the name: the same UC NAME can exist
 *  under multiple solution areas as DISTINCT rows with DISTINCT ids, and
 *  LinkUcAsset attaches to one specific id. We keep solutionArea so the dropdown
 *  can group by it (mirrors the Structured tree Industry→Sales Area→Use Case), so
 *  a repeated name under different areas is unambiguous. */
type UcOption = { solutionMapId: number; solutionArea: string; useCase: string };

interface NewAssetCreateModalProps {
  onClose: () => void;
  /**
   * Called with the new asset's id after a successful create. The parent
   * (NewAssetsPage) reloads its list and selects the new row IN PLACE — we do
   * NOT navigate to /ivt/assets/:id (AssetDetailPage is being deprecated).
   */
  onCreated: (assetId: string) => void;
}

type Mode = 'uc' | 'topic';

/**
 * NewAssetCreateModal — a NEW, dedicated create modal for NewAssetsPage,
 * replicating the v3 prototype `naCreateNew` (app.html:2745-2796 + helpers
 * naNewSwitchMode :3049, naNewUpdateUCs :3065, naCreateSubmit :3086).
 *
 * This is INTENTIONALLY a separate component from the legacy
 * AssetsPage/components/CreateAssetModal (which is being deprecated).
 *
 * Layout (matches naCreateNew exactly):
 *   - Title "新建 New Asset"
 *   - Asset Name * — free-text required input (autofocus)
 *   - 归属 — a two-pill toggle 行业 UC / Topic (local state, default 行业 UC):
 *       · 行业 UC mode: Industry <select> (onchange refills UC) + Use Case <select>
 *       · Topic   mode: Topic <select> + 子项 <select>  (DISABLED placeholders)
 *   - 创建 / 取消 buttons
 *
 * DATA / OP decisions (skill Step 3 — never invent a command):
 *   - Industry universe + per-industry UC = ONE REAL OP. GetSolutionMapAll
 *     (CR-282603940) returns every industry's full matrix in a single
 *     round-trip; the industry list is out.industries[].industry and each
 *     industry's UC names are derived from its entries[].useCase (dedup + sort).
 *     This REPLACES the previous ListIndustries + N×GetSolutionMap fan-out
 *     (1 + N HTTP calls, one per industry) that made the modal slow to open.
 *     The prototype's fetchUCData(/api/uc-full) shape (app.html:1164,3065-3072)
 *     is reference-only — the portal has no /api/uc-full op.
 *   - Topic mode data = the shared `topicClient` seam (src/ivt/client/topicClient),
 *     the SAME facade SolutionMapPage's topic feature uses. It currently resolves
 *     to mockTopicStore and flips to real Smithy commands in one place when the
 *     backend Topic op ships (solution-map-v3-design §2.2) — so the Topic + 子项
 *     <select>s show real (mock) topics + their instances, NOT a dead placeholder.
 *     We do NOT invent our own topics op and do NOT route topics through
 *     getIvtClient — we reuse the existing topicClient so there is ONE swap point.
 *
 * SUBMIT (skill Step 3 — resolve explicitly, no force-fit):
 *   - 行业 UC mode: CreateAssetCommand({ name, industry, useCase, origin:
 *     ivt_incubated, audience: undefined, ownerIs: undefined }). The updated
 *     CreateAsset contract (CR-282363544) added optional name/maturity/origin
 *     and relaxed industry/audience to optional, so the Asset Name IS now sent
 *     (was dropped before), origin is set to ivt_incubated (New Assets are the
 *     incubated slice), and maturity is omitted → server defaults to EXPLORING
 *     (假设期), matching prototype naCreateSubmit. AUDIENCE GAP: naCreateNew
 *     collects no 受众; audience is optional in the unified model, so undefined
 *     is type-legal and benign (no longer a divergence — the old modal's
 *     REQUIRES-audience was the legacy contract).
 *   - Topic mode: a TWO-STEP write (CreateAsset has no topic field, by design —
 *     see topic.smithy "CreateAsset → LinkTopicAsset"). Step 1: CreateAsset with
 *     NO industry AND NO useCase (topic-anchored assets have neither — the
 *     backend now allows useCase to be absent; sending the sub-topic name as a
 *     pseudo-useCase used to make it masquerade as a real Use Case downstream).
 *     Step 2: LinkTopicAsset(assetId, subTopicId) writes the
 *     topic_asset_link row. The link key is subTopicId (topic_instances.id), not
 *     a name — so the 子项 <select> options carry {subTopicName, subTopicId} and
 *     instances without an id are filtered out (cannot be linked). If step 2
 *     fails after step 1, the asset is KEPT (no rollback op; rollback is riskier)
 *     and the user is told it was created but not linked — LinkTopicAsset is
 *     idempotent so retrying from the detail page is safe.
 */
export default function NewAssetCreateModal({ onClose, onCreated }: NewAssetCreateModalProps) {
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  // industry -> 该行业 SolutionMap 的 UC 列表（每条带 solutionMapId + solutionArea，
  // 按 area 分组 → 同名 UC 跨 area 不被去重吞掉）。
  const [ucByIndustry, setUcByIndustry] = useState<Map<string, UcOption[]>>(new Map());
  const [loadingMap, setLoadingMap] = useState(true);

  const [name, setName] = useState('');
  const [mode, setMode] = useState<Mode>('uc');
  const [industry, setIndustry] = useState('');
  // The selected UC = its solutionMapId (the link key). '' = none chosen. Stored as
  // string in the <select> value, parsed back to number on submit.
  const [ucId, setUcId] = useState('');

  // Topic-mode data from the shared topicClient seam (mock today, real op later).
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicName, setTopicName] = useState('');
  const [topicItem, setTopicItem] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 拉 SolutionMap 全景（行业列表 + 每行业 UC）— 单次 GetSolutionMapAll（CR-282603940）
  // 一个往返返回所有行业的完整矩阵，取代旧的 ListIndustries + N×GetSolutionMap 扇出
  // (1 + N 个 HTTP 调用，行业越多打开 modal 越慢)。下游 industries / ucByIndustry
  // 两个 state 的形状不变，所以渲染/ucOptions/行业切换逻辑都不用动。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await getIvtClient().send(new GetSolutionMapAllCommand({}));
        if (cancelled) return;

        const all = out.industries ?? [];
        // industries 下拉的全集 = GetSolutionMapAll 返回的每个行业名，按字母序
        // （和 sortIndustries 同规则；这里 state 只需 industry 名）。
        const inds = all
          .map((i) => i.industry)
          .filter((n): n is string => !!n)
          .sort((a, b) => a.localeCompare(b, 'en'))
          .map((industry) => ({ industry }));
        setIndustries(inds);

        // 每行业 UC 列表 = 该行业 entries[]，每条保留 (solutionMapId=id, solutionArea,
        // useCase)。不再按名去重 —— 同名 UC 跨 solution area 是不同 solution_map 行、
        // 不同 id，去重会吞掉第二条并丢失其 id（LinkUcAsset 挂的正是那个 id）。丢弃缺
        // id / 缺名的条目（无法 link），以及 DEPRECATED 的 UC —— 读侧 UC Catalog 树
        // 在 view 模式隐藏 DEPRECATED（UcCatalogTree u.status !== 'DEPRECATED'），若允许
        // 挂到已废弃 UC，asset 会被读侧藏起来 = 又“挂不上”的症状。按 (area, useCase) 排序。
        const m = new Map<string, UcOption[]>();
        for (const i of all) {
          if (!i.industry) continue;
          const ucs: UcOption[] = (i.entries ?? [])
            .filter(
              (e): e is typeof e & { id: number; useCase: string } =>
                typeof e.id === 'number' && !!e.useCase && e.status !== 'DEPRECATED',
            )
            .map((e) => ({
              solutionMapId: e.id,
              solutionArea: e.solutionArea ?? '',
              useCase: e.useCase,
            }))
            .sort(
              (a, b) =>
                a.solutionArea.localeCompare(b.solutionArea, 'en') ||
                a.useCase.localeCompare(b.useCase, 'en'),
            );
          m.set(i.industry, ucs);
        }
        setUcByIndustry(m);
        if (inds[0]?.industry) setIndustry(inds[0].industry);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? '加载 Solution Map 失败');
      } finally {
        if (!cancelled) setLoadingMap(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ucOptions = useMemo(() => ucByIndustry.get(industry) ?? [], [ucByIndustry, industry]);

  // UC options grouped by solution area (for the <optgroup>s). Insertion order is
  // already (area, useCase)-sorted from the load effect, so groups come out sorted.
  const ucGroups = useMemo(() => {
    const groups = new Map<string, UcOption[]>();
    for (const o of ucOptions) {
      const g = groups.get(o.solutionArea);
      if (g) g.push(o);
      else groups.set(o.solutionArea, [o]);
    }
    return [...groups.entries()];
  }, [ucOptions]);

  // 行业切换时,重置 UC 选择（naNewUpdateUCs :3065 在原型里也是 onchange 重填 UC 下拉）
  useEffect(() => {
    setUcId('');
  }, [industry]);

  // Topic 列表来自共享 topicClient（与 SolutionMapPage 同一个门面;现为 mock,后端 op 落地后一处切换）。
  useEffect(() => {
    let cancelled = false;
    topicClient
      .list()
      .then((res) => {
        if (!cancelled) setTopics(res.topics ?? []);
      })
      .catch(() => {
        /* topic list is non-blocking; 行业 UC mode is unaffected */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 选中 topic 的子项 = {名字, subTopicId}。LinkTopicAsset 要 subTopicId
  // (topic_instances.id)；<select> 仍用名字做 value（稳定好读），提交时按名查回 id。
  // 没有 subTopicId 的实例（旧 mock 不带）被过滤掉——无 id 无法 link。
  const topicItemOptions = useMemo(
    () =>
      topics
        .find((t) => t.name === topicName)
        ?.instances?.filter(
          (i): i is TopicInstance & { subTopicName: string; subTopicId: number } =>
            !!i.subTopicName && typeof i.subTopicId === 'number',
        )
        .map((i) => ({ subTopicName: i.subTopicName, subTopicId: i.subTopicId })) ?? [],
    [topics, topicName],
  );

  // topic 切换时重置子项。
  useEffect(() => {
    setTopicItem('');
  }, [topicName]);

  // 行业 UC 模式: name + industry + 选中的 UC(带 solutionMapId)。Topic 模式: name + topic + 子项(有 subTopicId)。
  const selectedUc = useMemo(
    () => ucOptions.find((o) => String(o.solutionMapId) === ucId),
    [ucOptions, ucId],
  );
  const selectedSubTopicId = useMemo(
    () => topicItemOptions.find((o) => o.subTopicName === topicItem)?.subTopicId,
    [topicItemOptions, topicItem],
  );
  const canSubmit =
    !submitting &&
    !!name.trim() &&
    (mode === 'uc'
      ? !!industry && !!selectedUc
      : !!topicName && typeof selectedSubTopicId === 'number');

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const client = getIvtClient();
      // Topic-anchored assets have NO industry and NO UC — their classification
      // is the sub-topic link (LinkTopicAsset below), not a use case.
      const isTopic = mode === 'topic';
      const out = await client.send(
        new CreateAssetCommand({
          name: name.trim(),
          // Topic mode: omit both industry and useCase (topic-anchored). Sending
          // the sub-topic name as a pseudo-useCase made it masquerade as a real
          // Use Case in the Pipeline / Motion lists — the backend now allows
          // useCase to be absent, so send undefined. 行业 UC mode: real industry + UC.
          industry: isTopic ? undefined : industry,
          useCase: isTopic ? undefined : selectedUc?.useCase,
          // New Assets are the incubated slice of the unified-asset model.
          origin: AssetOrigin.IVT_INCUBATED,
          // maturity omitted → server defaults to EXPLORING (假设期).
          audience: undefined,
          ownerIs: undefined,
        }),
      );
      const newId = out.asset?.assetId;
      // assetId is nullable by contract (CreateAssetOutput.asset?.assetId). Without
      // this guard, an id-less response would skip BOTH link steps, close the modal,
      // and never call onCreated — a SILENT no-op that reproduces the very "asset not
      // linked to its UC" bug this fix targets. Throw into the outer catch so the
      // user sees an error instead (mirrors UcCatalogTree's create-then-link guard).
      if (!newId) throw new Error('创建失败：服务端未返回 assetId');

      // Topic mode = two-step: CreateAsset then LinkTopicAsset (no topic field on
      // CreateAsset). If the link fails AFTER the asset is created, keep the asset
      // (no rollback op; rollback is riskier) and tell the user it was created but
      // not linked — LinkTopicAsset is idempotent, so retrying from detail is safe.
      if (isTopic && typeof selectedSubTopicId === 'number') {
        try {
          await topicClient.linkTopicAsset({ assetId: newId, subTopicId: selectedSubTopicId });
        } catch (linkErr) {
          // "可在详情页重试关联" is only true if the retry can succeed. A denial here
          // means it cannot, so drop the retry advice and say what to ask for.
          const denied = denialHint(linkErr);
          setError(
            denied
              ? `Asset 已创建，但关联 Topic「${topicName} → ${topicItem}」失败：${denied}`
              : `Asset 已创建，但关联 Topic「${topicName} → ${topicItem}」失败：` +
                `${(linkErr as Error).message || '未知错误'}。可在详情页重试关联。`,
          );
          setSubmitting(false);
          // Still surface the created asset to the parent so the user can find it.
          onCreated(newId);
          return;
        }
      }

      // 行业 UC mode = also a TWO-STEP write: CreateAsset only writes the asset's own
      // industry/useCase columns — it does NOT create the uc_asset_link row that
      // makes the asset appear UNDER the UC (the Structured tree / UC arsenal read
      // AssetSummary.solutionMapLinks, which the backend projects from uc_asset_link,
      // NOT from assets.use_case). So we must LinkUcAsset by the UC's solutionMapId
      // (the same op UcCatalogTree uses). Same keep-on-failure policy as Topic mode:
      // LinkUcAsset is idempotent, so retrying from the detail page is safe.
      if (!isTopic && selectedUc) {
        try {
          await client.send(
            new LinkUcAssetCommand({
              assetId: newId,
              solutionMapId: selectedUc.solutionMapId,
              role: 'PRIMARY',
            }),
          );
        } catch (linkErr) {
          // Same as the Topic branch: a denial is not retryable.
          const denied = denialHint(linkErr);
          const what = `${selectedUc.solutionArea} / ${selectedUc.useCase}`;
          setError(
            denied
              ? `Asset 已创建，但关联 Use Case「${what}」失败：${denied}`
              : `Asset 已创建，但关联 Use Case「${what}」失败：` +
                `${(linkErr as Error).message || '未知错误'}。可在详情页重试关联。`,
          );
          setSubmitting(false);
          onCreated(newId);
          return;
        }
      }

      onClose();
      // Stay on the New Assets page: hand the new id to the parent, which reloads
      // the list and selects the new row IN PLACE.
      onCreated(newId);
    } catch (e) {
      setError(denialHint(e) ?? ((e as Error).message || '创建失败'));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="ivt-na-create__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="ivt-na-create" role="dialog" aria-modal="true">
        <div className="ivt-na-create__title">新建 New Asset</div>

        {loadingMap ? (
          <div className="ivt-na-create__loading">加载 Solution Map…</div>
        ) : (
          <>
            <label className="ivt-na-create__label" htmlFor="na-new-name">
              Asset Name *
            </label>
            <input
              id="na-new-name"
              className="ivt-na-create__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Smart Factory Assistant"
              disabled={submitting}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- matches prototype naCreateNew :2795 focus
              autoFocus
            />

            <div className="ivt-na-create__label">归属</div>
            <div className="ivt-na-create__toggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'uc'}
                className={`ivt-na-create__pill${
                  mode === 'uc' ? ' ivt-na-create__pill--uc-active' : ''
                }`}
                onClick={() => setMode('uc')}
                disabled={submitting}
              >
                行业 UC
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'topic'}
                className={`ivt-na-create__pill${
                  mode === 'topic' ? ' ivt-na-create__pill--topic-active' : ''
                }`}
                onClick={() => setMode('topic')}
                disabled={submitting}
              >
                Topic
              </button>
            </div>

            {mode === 'uc' ? (
              <div className="ivt-na-create__group">
                <div className="ivt-na-create__field">
                  <div className="ivt-na-create__sublabel">Industry</div>
                  <select
                    className="ivt-na-create__select"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    disabled={submitting}
                  >
                    {industries.map((i) => (
                      <option key={i.industry} value={i.industry ?? ''}>
                        {i.industry}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ivt-na-create__field">
                  <div className="ivt-na-create__sublabel">Use Case</div>
                  {/* Grouped by solution area (<optgroup>) — same shape as the
                      Structured tree Industry→Sales Area→Use Case. option value =
                      solutionMapId (the link key): a UC NAME can repeat across
                      areas, but the id is unique, so this disambiguates. */}
                  <select
                    className="ivt-na-create__select"
                    value={ucId}
                    onChange={(e) => setUcId(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">
                      {ucOptions.length > 0 ? '— 选择 UC —' : '（该行业 Solution Map 为空）'}
                    </option>
                    {ucGroups.map(([area, ucs]) => {
                      // Relabel the '_span_' sentinel to "Cross-Area" (shared helper);
                      // '' → 未分区. option value stays solutionMapId.
                      const groupLabel = area === SPAN_AREA ? displayAreaName(area) : area || '（未分区）';
                      return (
                        <optgroup key={groupLabel} label={groupLabel}>
                          {ucs.map((uc) => (
                            <option key={uc.solutionMapId} value={String(uc.solutionMapId)}>
                              {uc.useCase}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              </div>
            ) : (
              <div className="ivt-na-create__group">
                {/* Topic + 子项 use the shared topicClient seam (mock today, real
                    op later) — same facade as SolutionMapPage. */}
                <div className="ivt-na-create__field">
                  <div className="ivt-na-create__sublabel">Topic</div>
                  <select
                    className="ivt-na-create__select"
                    value={topicName}
                    onChange={(e) => setTopicName(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">— 可选 —</option>
                    {topics.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ivt-na-create__field">
                  <div className="ivt-na-create__sublabel">子项</div>
                  <select
                    className="ivt-na-create__select"
                    value={topicItem}
                    onChange={(e) => setTopicItem(e.target.value)}
                    disabled={submitting || !topicName}
                  >
                    <option value="">— 选择子项 —</option>
                    {topicItemOptions.map((o) => (
                      <option key={o.subTopicId} value={o.subTopicName}>
                        {o.subTopicName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {error && <div className="ivt-na-create__error">⚠️ {error}</div>}

            <div className="ivt-na-create__actions">
              {/* Topic-mode create is a TWO-STEP write: CreateAsset (no industry,
                  no useCase — topic-anchored) then LinkTopicAsset(assetId, subTopicId).
                  Enabled once name + topic + a sub-topic WITH an id are chosen. */}
              <button
                type="button"
                className="ivt-na-create__btn ivt-na-create__btn--primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? '创建中…' : '创建'}
              </button>
              <button
                type="button"
                className="ivt-na-create__btn"
                onClick={onClose}
                disabled={submitting}
              >
                取消
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
